import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { handleAsyncError } from '../../../actions/general';
import { getRoute } from '../../../actions/sessions';
import usePayNowHandoff from '../../../hooks/usePayNowHandoff';
import usePostHogExperiment from '../../../hooks/usePostHogExperiment';
import useSessionEntity from '../../../hooks/useSessionEntity';
import { formatCurrency } from '../../../lib/utils/formatters';
import PrimaryButton from '../../shared/buttons/PrimaryButton';
import Loading from '../../shared/Loading';

const PAYMENT_OPTIONS = {
  PAY_IN_FULL: 'pay_in_full',
  PAY_OVER_TIME: 'pay_over_time',
};

// Control offers both options; the test variant drops the choice and continues
// on Pay Over Time for the borrower
export const PAYMENT_CHOICE_FLAG = 'pay-now-vs-pay-later-option';
export const SKIP_PAYMENT_CHOICE_VARIANT = 'test-skip-pay-now-or-pay-later';

export default function ProductSelection() {
  const dispatch = useDispatch();
  const cart = useSessionEntity('shopping_cart');
  const isLoading = useSelector((state) => state.sessions.get('loading'));
  const [selectedOption, setSelectedOption] = useState(
    PAYMENT_OPTIONS.PAY_OVER_TIME
  );
  const { variant, resolved } = usePostHogExperiment(PAYMENT_CHOICE_FLAG);
  const [autoContinueFailed, setAutoContinueFailed] = useState(false);
  const [routing, setRouting] = useState(false);
  const autoContinued = useRef(false);
  const routeInFlight = useRef(false);
  const { startHandoff, submitting } = usePayNowHandoff({
    unavailableMessage: 'Please choose how you would like to pay again.',
    failureMessage: "We couldn't start Pay in Full. Please try again.",
  });

  const skipPaymentChoice =
    resolved && variant === SKIP_PAYMENT_CHOICE_VARIANT && !autoContinueFailed;

  // getRoute() leaves the shared loading flag alone, so Continue stays live
  // for the whole request unless this path disables it itself [PR 181]
  const continueOverTime = async () => {
    if (routeInFlight.current) return;

    routeInFlight.current = true;
    setRouting(true);

    try {
      // The route response swaps the page, so the guard deliberately holds
      // until unmount rather than releasing on success
      return await dispatch(getRoute());
    } catch (routeError) {
      routeInFlight.current = false;
      setRouting(false);
      dispatch(handleAsyncError(routeError));
    }
  };

  const handleContinue = async () => {
    if (selectedOption === PAYMENT_OPTIONS.PAY_OVER_TIME) {
      return continueOverTime();
    }

    return startHandoff();
  };

  // The test variant continues for the borrower, so it advances off the
  // resolved variant rather than a click. A failed route falls back to the
  // options instead of leaving the spinner up with nothing to retry.
  useEffect(() => {
    if (!skipPaymentChoice || autoContinued.current) return;

    autoContinued.current = true;
    dispatch(getRoute()).catch((routeError) => {
      setAutoContinueFailed(true);
      dispatch(handleAsyncError(routeError));
    });
  }, [skipPaymentChoice, dispatch]);

  // Withhold the options until the variant lands and while the test variant is
  // advancing — a test borrower who glimpsed them would be seeing control
  if (!resolved || skipPaymentChoice || !cart)
    return <Loading className="w-14 h-14" containerClass="my-24 sm:my-0" />;

  const orderTotal = formatCurrency(cart.grand_total);

  const payInFullBenefits = [
    { text: 'No fees' },
    { text: 'Immediate processing' },
    { text: 'No application needed' },
  ];

  const payOverTimeBenefits = [
    { text: '0% interest first 30 days' },
    { text: 'No early payoff fee' },
    { text: 'Build business credit' },
  ];

  const renderPaymentOption = (optionId, title, subtitle, benefits) => {
    const isSelected = selectedOption === optionId;

    return (
      <div className="group relative">
        <input
          id={optionId}
          type="radio"
          name="payment_option"
          value={optionId}
          checked={isSelected}
          onChange={() => setSelectedOption(optionId)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setSelectedOption(optionId);
            }
          }}
          aria-labelledby={`${optionId}-title`}
          aria-describedby={`${optionId}-description ${optionId}-benefits`}
          className="peer absolute left-4 top-5 h-5 w-5 cursor-pointer border-gray-300 text-primary-blue focus:ring-primary-blue"
        />
        <label
          htmlFor={optionId}
          className={classNames(
            'block cursor-pointer rounded-xl border-2 bg-white p-4 pl-12 transition-colors duration-150 group-hover:bg-secondary-blue/30 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-blue peer-focus-visible:ring-offset-2',
            {
              'border-primary-blue': isSelected,
              'border-gray-200 group-hover:border-primary-blue/40': !isSelected,
            }
          )}
        >
          <span
            id={`${optionId}-title`}
            className="block text-base font-semibold leading-6 text-primary sm:text-lg"
          >
            {title}
          </span>
          <span
            id={`${optionId}-description`}
            className="mt-1 block text-sm font-normal leading-5 text-secondary-gray"
          >
            {subtitle}
          </span>
          <span
            id={`${optionId}-benefits`}
            role="list"
            className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5"
          >
            {benefits.map((benefit) => (
              <span
                key={benefit.text}
                role="listitem"
                className="flex items-start gap-1.5"
              >
                <FontAwesomeIcon
                  icon={['fas', 'check']}
                  aria-hidden="true"
                  className="mt-1.5 shrink-0 text-[10px] text-success"
                />
                <span className="text-xs font-normal leading-5 text-gray-600">
                  {benefit.text}
                </span>
              </span>
            ))}
          </span>
        </label>
      </div>
    );
  };

  return (
    <div id="content" role="main" className="mx-6 sm:mx-auto max-w-2xl">
      <div className="my-4">
        <div className="mb-5 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="text-sm font-medium">Order Total</div>
          <div className="text-sm font-medium">{orderTotal}</div>
        </div>

        <fieldset className="min-w-0">
          <legend className="mb-3 text-lg font-semibold leading-6 text-primary sm:text-xl">
            How would you like to pay?
          </legend>

          <div className="mb-3 space-y-3">
            {renderPaymentOption(
              PAYMENT_OPTIONS.PAY_OVER_TIME,
              'Pay Over Time',
              'Net Terms, Monthly Installments, or Pay in 4',
              payOverTimeBenefits
            )}
            {renderPaymentOption(
              PAYMENT_OPTIONS.PAY_IN_FULL,
              'Pay in Full',
              '1 payment today from your business checking account',
              payInFullBenefits
            )}
          </div>
        </fieldset>

        <PrimaryButton
          buttonText="Continue"
          buttonColor="bg-primary-blue"
          hoverClass="hover:bg-primary-blue-hover"
          onClick={handleContinue}
          disabled={isLoading || submitting || routing}
          isLoading={isLoading || submitting || routing}
        />
      </div>
    </div>
  );
}
